/**
 * Radial-gradient glow helpers for the Spotlight V2 slide backgrounds.
 *
 * Responsibility is split the same way as the Header-Highlight banner (see
 * `molecules/PromotionBanner/glows.ts`): the FE owns a fixed set of glow
 * *positions*, the backend owns the glow *colors*. Unlike the banner's
 * fixed-px circles, each glow here is sized as a resolution-independent
 * ellipse in percentages, so the same set works at any slide height.
 */

/**
 * A single spotlight glow blob, sized independently of resolution. Everything
 * is a percentage of the background box, so the same set works for the tall
 * mobile card and the wide desktop split-panel without a magic pixel constant.
 * Centers may fall outside [0, 100] to let a large bloom bleed in from an edge.
 */
export interface EllipseGlow {
  /** Horizontal center, as a percentage of the box width (0 = left). */
  cx: number;
  /** Vertical center, as a percentage of the box height (0 = top). */
  cy: number;
  /** Horizontal radius, as a percentage of the box width. */
  rx: number;
  /** Vertical radius, as a percentage of the box height. */
  ry: number;
}

/**
 * Fixed FE glow positions for the spotlight campaign background, per breakpoint.
 *
 * Lifted from the Figma "Spotlight campaign/ background" frame (file
 * `uZF8i1AttEzmv9ZtD3DBaj`, mobile node `7488:31073`), which contains three
 * `<ellipse>` layers over the solid base. Their canvas rects were converted to
 * percentages of the 311×311 card box (center = (x + w/2) / 311, radius =
 * (w/2) / 311); centers fall outside [0, 100] where a bloom bleeds in from an
 * edge — matching the design 1:1:
 *   - Ellipse 42 (top):          x=10,   y=-176, w=307, h=273
 *   - Ellipse 43 (left-middle):  x=-122, y=69,   w=222, h=221
 *   - Ellipse 49 (bottom-right): x=117,  y=119,  w=270, h=270
 *
 * The colour-only mobile *card* reuses this same set: Figma places an
 * identical ellipse frame on the card (node `7488:32774`), so the card blooms
 * roughly line up with the ambient ones behind it.
 *
 * Colors are backend-driven: `gradientColors` is the `[inner, outer]` stop
 * pair of one shared gradient, applied to every slot here (see
 * {@link buildSpotlightGlowsCss}).
 */
export const SPOTLIGHT_GLOWS_MOBILE: EllipseGlow[] = [
  // Ellipse 42 — large bloom bleeding in from the top edge.
  {cx: 52.6, cy: -12.7, rx: 49.4, ry: 43.9},
  // Ellipse 43 — bloom bleeding in from the left edge, vertical middle.
  {cx: -3.5, cy: 57.7, rx: 35.7, ry: 35.5},
  // Ellipse 49 — bloom in the bottom-right corner.
  {cx: 81.0, cy: 81.7, rx: 43.4, ry: 43.4},
];

/**
 * Desktop spotlight glow positions, applied both to the full-width banner
 * background (behind the left column) and to the right campaign panel.
 *
 * The desktop "Spotlight campaign/ background" in Figma (file
 * `uZF8i1AttEzmv9ZtD3DBaj`, e.g. node `7489:49874`) is a *baked raster* glow
 * over the `#ff7fca` base — no vector ellipses to lift — so, unlike the mobile
 * set, these coordinates were tuned to reproduce the sampled look: a soft
 * light-pink wash with a brighter bloom at top-center and the two bottom
 * corners. Verified by screenshot diff against the Figma variants; adjust
 * during design QA if the raster is later vectorized.
 */
export const SPOTLIGHT_GLOWS_DESKTOP: EllipseGlow[] = [
  // Top-center bloom.
  {cx: 50, cy: -25, rx: 45, ry: 90},
  // Bottom-left bloom.
  {cx: 6, cy: 118, rx: 38, ry: 95},
  // Bottom-right bloom.
  {cx: 94, cy: 118, rx: 38, ry: 95},
];

/**
 * Glow positions for the colour-only desktop *card* (the right panel). Unlike
 * the raster desktop background, the card carries its own vector "Spotlight
 * campaign/ background" frame in Figma (file `uZF8i1AttEzmv9ZtD3DBaj`, node
 * `7489:49829`, 934×455 box) — four `<ellipse>` layers converted to
 * percentages exactly like {@link SPOTLIGHT_GLOWS_MOBILE}:
 *   - Ellipse 42 (top-center):   x=235,  y=-293, w=520,   h=463
 *   - Ellipse 50 (top-left):     x=-331, y=-260, w=447.7, h=398.1
 *   - Ellipse 43 (bottom-left):  x=0,    y=208,  w=540,   h=537
 *   - Ellipse 49 (right-middle): x=755,  y=98,   w=393.75, h=393.75
 */
export const SPOTLIGHT_CARD_GLOWS_DESKTOP: EllipseGlow[] = [
  // Ellipse 42 — bloom bleeding in from the top edge, horizontal center.
  {cx: 53.0, cy: -13.5, rx: 27.8, ry: 50.9},
  // Ellipse 50 — bloom in the top-left corner.
  {cx: -11.5, cy: -13.4, rx: 24.0, ry: 43.8},
  // Ellipse 43 — bloom bleeding in from the bottom-left.
  {cx: 28.9, cy: 104.7, rx: 28.9, ry: 59.0},
  // Ellipse 49 — bloom bleeding in from the right edge, vertical middle.
  {cx: 101.9, cy: 64.8, rx: 21.1, ry: 43.3},
];

/**
 * Build a CSS `background-image` value for the spotlight glow background: one
 * soft radial glow per FE slot, so the solid base color shows through between
 * blobs.
 *
 * `colors` is the stop pair of ONE shared radial gradient — `[inner, outer?]`
 * — applied to EVERY glow slot, exactly like the banner-mode helper in
 * `molecules/PromotionBanner/glows.ts`. That mirrors the schema contract
 * (`gradientColors` "matches BannerService.HighlightBanner") and the Figma
 * source, where every bloom ellipse carries the same `color → alpha-0`
 * gradient. A missing outer stop fades to `transparent`. Colors are
 * ready-to-use CSS color strings (e.g. `rgba(...)`) produced in the logic
 * layer — this presentational helper never sees the backend's `GradientColor`
 * struct. Empty/undefined colors ⇒ `'none'` (solid background, no glow),
 * matching the banner helper's empty-handling.
 */
export const buildSpotlightGlowsCss = (
  glows: EllipseGlow[],
  colors: string[] | undefined,
): string => {
  if (!colors || colors.length === 0 || glows.length === 0) return 'none';
  const [inner, outer] = colors;
  const fade = outer ?? 'transparent';
  return glows
    .map(
      glow =>
        `radial-gradient(ellipse ${glow.rx}% ${glow.ry}% at ${glow.cx}% ${glow.cy}%, ${inner} 0%, ${fade} 100%)`,
    )
    .join(', ');
};
